import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Sparkles, Heart, Bell, Wand2, Armchair } from "lucide-react";

interface GeneratingDesignProps {
  preview: string | null;
  /** Live server-reported stage, e.g. "Fixing a few room details…". */
  stage?: string | null;
}

const TRACK_TIP = {
  icon: Bell,
  text: "Saved pieces can be price-tracked — we'll email you when one drops.",
};

const TIPS = [
  {
    icon: Sparkles,
    text: "You'll get a full room design plus real furniture matched to your budget and style.",
  },
  {
    icon: Heart,
    text: "Tap the heart on any piece to save it to your list.",
  },
  {
    icon: Wand2,
    text: "Not quite right? Refine the design with a sentence — 3 refinements per project on the free plan.",
  },
  {
    icon: Armchair,
    text: "We spot the furniture already in your room, so you choose what to keep and what to replace.",
  },
];

const GeneratingDesign = ({ preview, stage }: GeneratingDesignProps) => {
  const reduceMotion = useReducedMotion();
  const [tipIndex, setTipIndex] = useState(0);

  const tips = useMemo(
    () => [...TIPS.slice(0, 2), TRACK_TIP, ...TIPS.slice(2)],
    [],
  );

  useEffect(() => {
    if (reduceMotion) return;
    const id = window.setInterval(() => {
      setTipIndex((i) => (i + 1) % tips.length);
    }, 4000);
    return () => window.clearInterval(id);
  }, [reduceMotion, tips.length]);

  const activeTip = tips[tipIndex % tips.length];
  const ActiveIcon = activeTip.icon;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="flex flex-col items-center gap-8 w-full max-w-md mx-auto text-center"
    >
      <div className="relative">
        <motion.div
          animate={{ scale: [1, 1.03, 1] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          className="relative w-48 h-48 overflow-hidden border border-border shadow-lg bg-muted"
        >
          {preview ? (
            <>
              <img
                src={preview}
                alt="Your room"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-foreground/10 backdrop-blur-[1px]" />
            </>
          ) : (
            <div className="w-full h-full bg-muted animate-pulse" />
          )}
        </motion.div>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
          className="absolute -top-3 -right-3 w-10 h-10 rounded-full bg-gradient-cta flex items-center justify-center shadow-elegant"
        >
          <Sparkles className="w-5 h-5 text-primary-foreground" />
        </motion.div>
      </div>

      <div className="space-y-2">
        <h2 className="text-2xl font-display text-foreground">
          Designing your room…
        </h2>
        <AnimatePresence mode="wait">
          <motion.p
            key={stage ?? "default"}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.3 }}
            className="text-muted-foreground"
          >
            {stage || "Our AI is rendering your new space."}
          </motion.p>
        </AnimatePresence>
      </div>

      <div className="w-full max-w-xs">
        <div className="h-px bg-muted overflow-hidden">
          <motion.div
            className="h-full bg-gradient-cta"
            animate={{ x: ["-100%", "250%"] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }}
            style={{ width: "40%" }}
          />
        </div>
      </div>

      {reduceMotion ? (
        <ul className="w-full max-w-xs space-y-2.5 text-left">
          {TIPS.map(({ icon: Icon, text }) => (
            <li key={text} className="flex items-start gap-2.5">
              <Icon className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
              <span className="text-xs text-muted-foreground leading-relaxed">
                {text}
              </span>
            </li>
          ))}
        </ul>
      ) : (
        <div className="w-full max-w-xs h-14 flex items-start justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={tipIndex}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.35 }}
              className="flex items-start gap-2.5 text-left"
            >
              <ActiveIcon className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground leading-relaxed">
                {activeTip.text}
              </p>
            </motion.div>
          </AnimatePresence>
        </div>
      )}
    </motion.div>
  );
};

export default GeneratingDesign;
